import React from 'react';

import styled from '@emotion/styled';

import { BREAKPOINTS, Box, COLORS, FontWeight, Text } from '@clutter/clean';

import { RoundCheckmark } from '@root/components/icons/round_checkmark';
import { XButton } from '@root/components/icons/x_button';

import { Block, BlockProps } from '@shared/page_builder/block';

type ListItem = { label: string; values: boolean[] };

const Table = styled.table`
  margin: 0 auto;

  & th {
    text-align: center;
    height: 48px;
    padding: 0 4px;
  }

  & tr:not(:last-child) td {
    border-bottom: 1px solid ${COLORS.grayBorder};
  }

  & tbody td {
    height: 48px;
    min-width: 75px;

    &:not(:first-of-type) {
      text-align: center;
    }

    &:nth-of-type(2) {
      background: ${COLORS.tealBackground};
    }

    @media (min-width: ${BREAKPOINTS.SM}) {
      min-width: 140px;
    }
  }
`;

const Row = ({ item }: { item: ListItem }) => (
  <tr>
    <Box padding={['0 24px', '0 32px']} as="td">
      <Text.Caption weight={FontWeight.Medium}>{item.label}</Text.Caption>
    </Box>
    {item.values.map((checked, i) => (
      <td key={i}>
        {checked ? (
          <RoundCheckmark
            fillColor={i === 0 ? COLORS.tealPrimary : COLORS.dew}
            height={16}
            width={16}
          />
        ) : (
          <XButton
            fillColor={i === 0 ? COLORS.tealPrimary : COLORS.dew}
            height={10}
            width={10}
            strokeWidth={2}
          />
        )}
      </td>
    ))}
  </tr>
);

export const ComparisonGrid: React.FC<
  {
    title: React.ReactNode;
    headers: string[];
    listItems: ListItem[];
  } & BlockProps
> = ({ title, headers, listItems, ...blockProps }) => {
  return (
    <Block {...blockProps}>
      <Box margin="0 0 32px" textAlign="center">
        <Text.Title size="large">{title}</Text.Title>
      </Box>
      <Table>
        <thead>
          <tr>
            <th>{/* I am intentionally blank */}</th>
            {headers.map((h, i) => (
              <th key={i}>
                <Text.Body weight={FontWeight.Medium}>{h}</Text.Body>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listItems.map((item) => (
            <Row key={item.label} item={item} />
          ))}
        </tbody>
      </Table>
    </Block>
  );
};
